import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCompanyPayIns } from '../../../../../../lib/api/conciliations';
import BankAccountDialog from '../../../../../../lib/common/BankAccountDialog';
import GlobalSearcher, {
  Filter,
} from '../../../../../../lib/common/GlobalSearcher';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../../lib/designSystem/TableWithPagination';
import {
  formatDate,
  hasAnyPermissions,
  showAlert,
} from '../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../services/redux/authSlice';
import PaymentConciliationDialog from './components/Conciliation';
import Payment from './components/Payment';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'id', label: 'ID' },
  { key: 'businessOriginName', label: 'Cuenta Origen' },
  { key: 'businessOriginIdentifier', label: 'Rut Origen' },
  { key: 'businessDestinationName', label: 'Cuenta Destino' },
  { key: 'businessDestinationIdentifier', label: 'Rut Destino' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'amountConciliated', label: 'Monto conciliado', type: 'money' },
  { key: 'statusTranslated', label: 'Estado', type: 'conciliation-status' },
  { key: 'createdAt', label: 'Fecha de creación', type: 'date' },
];

const PayIns: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openBankAccountModal, setOpenBankAccountModal] = useState(false);
  const [payIn, setPayIn] = useState<any>({});
  const [payIns, setPayIns] = useState<any[]>([]);
  const [openConciliationDialog, setOpenConciliationDialog] = useState(false);

  const [status, setStatus] = useState<any[]>([
    { value: 'PENDING', label: 'Pendiente' },
  ]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [businessOriginIdentifier, setBusinessOriginIdentifier] = useState('');
  const [businessDestinationIdentifier, setBusinessDestinationIdentifier] =
    useState('');
  const [businessOriginName, setBusinessOriginName] = useState('');
  const [businessDestinationName, setBusinessDestinationName] = useState('');

  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (status && status.length > 0) {
      const statusValues = status.map((item: any) =>
        typeof item === 'string' ? item : item.value,
      );
      const statusParam = statusValues.join(',');
      query += `status=${statusParam}&`;
    }
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessOriginIdentifier)
      query += `businessOriginIdentifier=${businessOriginIdentifier}&`;
    if (businessDestinationIdentifier)
      query += `businessDestinationIdentifier=${businessDestinationIdentifier}&`;
    if (businessOriginName)
      query += `businessOriginName=${businessOriginName}&`;
    if (businessDestinationName)
      query += `businessDestinationName=${businessDestinationName}&`;

    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const handleConciliate = (id: number) => {
    const payIn = payIns.find((x) => x.id === id);
    if (payIn) {
      setPayIn(payIn);
      setOpenConciliationDialog(true);
    }
  };

  const handleNewPayment = () => {
    const newPayment = {
      amount: 0,
      bankAccountOriginId: 0,
      bankAccountDestinationId: 0,
      createdAt: formatDate(new Date()),
    };
    setPayIn(newPayment);
    setOpenPaymentModal(true);
  };

  const canShowAction = () => {
    if (
      hasAnyPermissions(user, [
        'financing_payIn_conciliate',
        'financing_payIn_delete',
        'financing_payIn_reverse',
        'financing_fnc_new',
      ])
    )
      return true;

    return false;
  };

  const actions = [
    {
      action: handleConciliate,
      actionLabel: 'Conciliación',
      canShow: canShowAction,
    },
  ];

  const fetchPayIns = async (
    {
      page,
      pageSize,
      id,
    }: {
      page: number;
      pageSize: number;
      id?: number;
    } = { page: 0, pageSize: PAGE_SIZE },
  ) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const payInsResponse = await getCompanyPayIns(dispatch, query);
      setPayIns(payInsResponse.data);
      setPagination(payInsResponse.pagination);

      // Si hay un payIn abierto en el diálogo, actualizarlo con los nuevos datos
      if (openConciliationDialog && payIn.id) {
        const updatedPayIn = payInsResponse.data.find(
          (x: any) => x.id === payIn.id,
        );
        if (updatedPayIn) {
          setPayIn(updatedPayIn);
        }
      }

      if (id) {
        handleConciliate(id);
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'No se pudieron obtener los pagos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchPayIns({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha de inicio',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      type: 'date',
      label: 'Fecha de fin',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      type: 'text',
      label: 'Rut origen',
      value: businessOriginIdentifier,
      setter: setBusinessOriginIdentifier,
    },
    {
      type: 'autocomplete',
      label: 'Nombre origen',
      value: businessOriginName,
      setter: setBusinessOriginName,
      key: 'businessOriginName',
    },
    {
      type: 'text',
      label: 'Rut destino',
      value: businessDestinationIdentifier,
      setter: setBusinessDestinationIdentifier,
    },
    {
      type: 'autocomplete',
      label: 'Nombre destino',
      value: businessDestinationName,
      setter: setBusinessDestinationName,
      key: 'businessDestinationName',
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: [
        { label: 'Pendiente', value: 'PENDING' },
        { label: 'Conciliado', value: 'CONCILIATED' },
        { label: 'Rechazado', value: 'REJECTED' },
        { label: 'En proceso', value: 'IN_PROCESS' },
        { label: 'Completado', value: 'COMPLETED' },
        { label: 'Cancelado', value: 'CANCELLED' },
      ],
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Nuevo pago',
      action: () => handleNewPayment(),
      color: 'primary',
      disabled: !hasAnyPermissions(user, ['financing_payIn_new']),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá podrás ver todos los pagos recibidos a tus cuentas registradas."
        variant="body1"
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={fetchPayIns}
                dispatch={dispatch}
                disableSearch={loading}
              />
            ),
          },
          ...buttons,
        ]}
      />
      <TableWithPagination
        dataHeaders={headers}
        data={payIns}
        rowsPerPageDefault={100}
        actions={actions}
        showActions
        exportToFile="xlsx"
        setData={setPayIns}
        searchData={fetchPayIns}
        pagination={pagination}
        setPagination={setPagination}
        lastElement
      />
      {openPaymentModal && (
        <Payment
          open={openPaymentModal}
          setOpen={setOpenPaymentModal}
          payIn={payIn}
          setPayIn={setPayIn}
          setOpenBankAccountModal={setOpenBankAccountModal}
          setUp={fetchPayIns}
          pagination={pagination}
        />
      )}
      {openBankAccountModal && (
        <BankAccountDialog
          open={openBankAccountModal}
          setOpen={setOpenBankAccountModal}
          handleSuccessExit={() => setOpenPaymentModal(true)}
        />
      )}
      {openConciliationDialog && (
        <PaymentConciliationDialog
          open={openConciliationDialog}
          setOpen={setOpenConciliationDialog}
          payIn={payIn}
          setPayIn={setPayIn}
          setUp={fetchPayIns}
          pagination={pagination}
          setOpenBankAccountModal={setOpenBankAccountModal}
        />
      )}
    </div>
  );
};

export default PayIns;
